import { flow, pipe } from 'fp-ts/lib/function';
import {
  addVersionForUpdate,
  createAuthorizedGetRequest,
  createAuthorizedPostMultipartRequest,
  createAuthorizedPutRequest,
  decode,
  decodeVersioned,
  fetchApi, setBody,
  setBodyFormData,
  Versioned,
} from '@/services/api';
import { chain, map } from 'fp-ts/TaskEither';
import { SsoConfiguration } from '@/entities/sso-configuration';
import {
  SamlConfiguration,
  ssoConfigurationCodec,
  ssoConfigurationResponseCodec,
  ssoErrorLogListCodec,
} from '@/services/api/resources/sso-configuration/codec';

const baseUrl = '/v2/organizations';
const recordUrl = (id: number): string => `${baseUrl}/${id}/sso`;
const recordErrorLogUrl = (id: number): string => `${baseUrl}/${id}/scim-logs`;

export function mapSsoToFormData(data: SsoConfiguration) {
  const formData = new FormData();
  const configuration = <SamlConfiguration> data.configuration;

  formData.append('organizationId', String(data.organizationId));
  formData.append('identityProviderType', String(data.identityProviderType));
  formData.append('jitProvisioning', String(data.jitProvisioning));
  formData.append('scimEnabled', String(data.scimEnabled));
  formData.append('scimRoleMappingEnabled', String(data.scimRoleMappingEnabled));
  if (data.scimAccessKey) {
    formData.append('scimAccessKey', data.scimAccessKey);
  }
  if (data.scimGroupAttribute) {
    formData.append('scimGroupAttribute', data.scimGroupAttribute);
  }
  if (data.scimSchemaNamespace) {
    formData.append('scimSchemaNamespace', data.scimSchemaNamespace);
  }
  if (data.defaultRole) {
    formData.append('defaultRole', String(data.defaultRole));
  }
  formData.append('organizationId', String(data.organizationId));
  formData.append('configuration[entityId]', String(configuration.entityId));
  formData.append('configuration[nameIdFormat]', String(configuration.nameIdFormat));
  formData.append('configuration[authorizationUri]', String(configuration.authorizationUri));
  formData.append('configuration[singleSignOnService]', String(configuration.singleSignOnService));
  formData.append('configuration[singleLogoutService]', String(configuration.singleLogoutService));

  if (configuration.certificate) {
    formData.append('configuration[certificate]', <Blob>configuration.certificate);
  }

  return formData;
}

export const updateOAuth = (id: number, ssoConfiguration: Versioned<SsoConfiguration>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(ssoConfiguration.version)),
  map(setBody(ssoConfigurationCodec.encode(ssoConfiguration.data))),
  chain(fetchApi),
);

export const updateSaml = (id: number, ssoConfiguration: Versioned<SsoConfiguration>) => pipe(
  id,
  recordUrl,
  createAuthorizedPostMultipartRequest,
  map(addVersionForUpdate(ssoConfiguration.version)),
  map(setBodyFormData(mapSsoToFormData(ssoConfigurationCodec.encode(ssoConfiguration.data)))),
  chain(fetchApi),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(ssoConfigurationResponseCodec)),
);

export const getErrorLogs = (id: number) => pipe(
  id,
  recordErrorLogUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(ssoErrorLogListCodec)),
);
